import { useEffect, useMemo, useState } from 'react';
import { Batch, RecordWithProtocol } from '../../types/sharedTypeImpl.ts';

export const NOT_SPECIFIED_MEASUREMENT_VARIANT = '__NOT_SPECIFIED__';

/* Data range selection options */
const rangeTypes = ['All', 'Batch', 'Order number', 'Date'] as const;
type RangeType = (typeof rangeTypes)[number];

interface BatchOption {
    index: number;
    start: Date;
    measurementCount: number;
}

interface OrderNumberOption {
    orderNumber: string;
    measurementCount: number;
}

export interface RangeOptions {
    rangeTypes: RangeType[];
    batches: BatchOption[];
    orderNumbers: OrderNumberOption[];
    measurementCount: number;
    measurementVariants: { name: string; available: boolean }[];
}

/* Current data range selection */
export interface RangeSelection {
    rangeType: RangeType;
    batchIndex: number | null;
    orderNumber: string | null;
    dateRange: {
        start: Date | null;
        end: Date | null;
    };
    measurementVariant: string | null;
}

interface Props {
    allRecords: RecordWithProtocol[] | null;
    recordsPerBatch: RecordWithProtocol[][] | null;
    batches: Batch[] | undefined;
}

export function useRangeOptions({ allRecords, recordsPerBatch, batches }: Props) {
    const [rangeSelection, setRangeSelection] = useState<RangeSelection | null>(null);

    // Prepare range selection options
    const rangeOptions: RangeOptions | null = useMemo(() => {
        if (!allRecords || !recordsPerBatch || !batches) {
            return null;
        }

        const batchOptionsRaw =
            batches?.map((batch, index) => ({
                index,
                start: batch.start,
                measurementCount: recordsPerBatch?.[index]?.length ?? 0,
            })) ?? [];
        const batchOptions = batchOptionsRaw.filter((it) => it.measurementCount > 0).reverse(); //newest to oldest

        // Get order numbers in the order that they appear in allRecords
        const orderNumberCounts = new Map<string, number>();
        for (const record of allRecords) {
            if (!record.orderNumber) continue;
            orderNumberCounts.set(record.orderNumber, (orderNumberCounts.get(record.orderNumber) ?? 0) + 1);
        }
        const orderNumberOptionsRaw = Array.from(orderNumberCounts, ([orderNumber, count]) => ({
            orderNumber,
            measurementCount: count,
        }));
        const orderNumberOptions = orderNumberOptionsRaw.filter((it) => it.measurementCount > 0).reverse(); //newest to oldest

        const availableVariantSet = new Set<string>();
        let hasUnspecifiedVariant = false;
        for (const record of allRecords) {
            const variant = record.measurementVariant?.trim();
            if (variant) {
                availableVariantSet.add(variant);
            } else {
                hasUnspecifiedVariant = true;
            }
        }
        const allVariantNames = new Set(['A', 'B', ...availableVariantSet]);
        const measurementVariants = Array.from(allVariantNames)
            .sort()
            .map((name) => ({ name, available: availableVariantSet.has(name) }));
        measurementVariants.push({ name: NOT_SPECIFIED_MEASUREMENT_VARIANT, available: hasUnspecifiedVariant });

        return {
            rangeTypes: [...rangeTypes],
            batches: batchOptions,
            orderNumbers: orderNumberOptions,
            measurementCount: allRecords.length,
            measurementVariants,
        };
    }, [allRecords, recordsPerBatch, batches]);

    // Set initial range selection
    useEffect(() => {
        if (!rangeOptions || rangeSelection) {
            return undefined;
        }

        const batches = rangeOptions.batches;
        const orderNumbers = rangeOptions.orderNumbers;
        setRangeSelection({
            rangeType: 'All',
            batchIndex: batches.length > 0 ? batches[0]!.index : null,
            orderNumber: orderNumbers.length > 0 ? orderNumbers[0]!.orderNumber : null,
            dateRange: {
                start: null,
                end: null,
            },
            measurementVariant: null,
        });
    }, [rangeOptions, rangeSelection]);

    return {
        rangeOptions,
        rangeSelection,
        setRangeSelection,
    };
};
